import React from 'react';
import PropTypes from 'prop-types';

/**
 * Reusable Loading Component
 * 
 * @param {string} size - Size of the spinner: 'sm', 'md', 'lg', 'xl'
 * @param {string} text - Optional loading text to display
 * @param {boolean} fullScreen - Whether to take up full screen
 * @param {string} color - Color theme: 'blue', 'green', 'red', 'purple', 'gray'
 */
export function Loading({ 
  size = 'md', 
  text = '', 
  fullScreen = false,
  color = 'blue'
}) {
  const sizeClasses = {
    sm: 'h-8 w-8 border-2',
    md: 'h-16 w-16 border-4',
    lg: 'h-24 w-24 border-4',
    xl: 'h-32 w-32 border-4'
  };

  const colorClasses = {
    blue: 'border-blue-500 border-t-transparent',
    green: 'border-green-500 border-t-transparent',
    red: 'border-red-500 border-t-transparent',
    purple: 'border-purple-500 border-t-transparent',
    gray: 'border-gray-500 border-t-transparent'
  };

  const textSizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  const containerClass = fullScreen 
    ? 'flex flex-col justify-center items-center min-h-screen bg-gray-50'
    : 'flex flex-col justify-center items-center py-12';

  return (
    <div className={containerClass}>
      <div 
        className={`animate-spin rounded-full ${sizeClasses[size]} ${colorClasses[color]}`}
        role="status"
        aria-label="Loading"
      />
      {text && (
        <p className={`mt-4 text-gray-600 ${textSizes[size]}`}>
          {text}
        </p>
      )}
    </div>
  );
}

Loading.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  text: PropTypes.string,
  fullScreen: PropTypes.bool,
  color: PropTypes.oneOf(['blue', 'green', 'red', 'purple', 'gray'])
};

export default Loading;